
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { services } from '@/data/services';
import { trackEvent, trackLead } from '@/lib/analytics';

const bookingSchema = z.object({
  name: z.string().min(2, 'Ім\'я повинно містити мінімум 2 символи'),
  phone: z.string().min(10, 'Введіть коректний номер телефону'),
  service: z.string().min(1, 'Оберіть послугу'),
  date: z.string().min(1, 'Оберіть дату'),
  time: z.string().min(1, 'Оберіть час')
});

function BookingForm() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      name: '',
      phone: '',
      service: '',
      date: '',
      time: ''
    }
  });

  const selectedService = watch('service');

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 400));

    const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
    const newBooking = {
      ...data,
      id: Date.now(),
      createdAt: new Date().toISOString()
    };
    bookings.push(newBooking);
    localStorage.setItem('bookings', JSON.stringify(bookings));

    trackLead('booking_form', { service_id: data.service });
    trackEvent('book_appointment', {
      event_category: 'conversion',
      event_label: data.service,
    });

    toast.success("Запис успішно створено. Ми зв'яжемося з вами найближчим часом.");
    reset();
    setIsSubmitting(false);
    navigate('/spasibo');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" id="order-form">
      <div className="space-y-2">
        <Label htmlFor="name">Ім'я</Label>
        <Input
          id="name"
          type="text"
          placeholder="Ваше ім'я"
          className="text-foreground"
          autoComplete="name"
          {...register('name')}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Телефон</Label>
        <Input
          id="phone"
          type="tel"
          placeholder="+380 XX XXX XX XX"
          className="text-foreground"
          autoComplete="tel"
          {...register('phone')}
        />
        {errors.phone && (
          <p className="text-sm text-destructive">{errors.phone.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="service">Послуга</Label>
        <Select
          value={selectedService}
          onValueChange={(value) => setValue('service', value)}
        >
          <SelectTrigger id="service" className="text-foreground">
            <SelectValue placeholder="Оберіть послугу" />
          </SelectTrigger>
          <SelectContent>
            {services.map((service) => (
              <SelectItem key={service.id} value={service.id}>
                {service.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.service && (
          <p className="text-sm text-destructive">{errors.service.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="date">Дата</Label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              id="date"
              type="date"
              className="pl-10 text-foreground"
              min={new Date().toISOString().split('T')[0]}
              {...register('date')}
            />
          </div>
          {errors.date && (
            <p className="text-sm text-destructive">{errors.date.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="time">Час</Label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              id="time"
              type="time"
              className="pl-10 text-foreground"
              min="08:00"
              max="20:00"
              {...register('time')}
            />
          </div>
          {errors.time && (
            <p className="text-sm text-destructive">{errors.time.message}</p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Обробка...' : 'Записатись'}
      </Button>
    </form>
  );
}

export default BookingForm;
